/**
 * NgRX Entity Adapters for Tickets Module
 * 
 * Three separate adapters for different ticket list types:
 * - Single tickets (user's own tickets)
 * - Syndicate tickets (syndicate portions)
 * - Group tickets (group ticket invitations)
 */

import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { TicketsScope } from './tickets.model';

/**
 * Adapter for single/solo tickets.
 * sortComparer: false — display order is determined by the backend API response.
 * The previous date-based sort was not intentional product behaviour; it was set
 * during the entity refactor but masked because the legacy state.list (no comparer)
 * was used for display. Now that entity selectors drive the UI we preserve
 * backend order.
 */
export const singleTicketAdapter: EntityAdapter<TicketsScope.TicketItem> =
  createEntityAdapter<TicketsScope.TicketItem>({
    selectId: (ticket: TicketsScope.TicketItem) => ticket.stringified_id,
    sortComparer: false,
  });

/**
 * Adapter for syndicate tickets.
 * sortComparer: false — same reasoning as singleTicketAdapter above.
 */
export const syndicateTicketAdapter: EntityAdapter<TicketsScope.TicketItem> =
  createEntityAdapter<TicketsScope.TicketItem>({
    selectId: (ticket: TicketsScope.TicketItem) => ticket.stringified_id,
    sortComparer: false,
  });

/**
 * Adapter for group tickets
 * Different entity type (GroupTicketListItem)
 */
export const groupTicketAdapter: EntityAdapter<TicketsScope.GroupTicketListItem> = 
  createEntityAdapter<TicketsScope.GroupTicketListItem>({
    selectId: (ticket: TicketsScope.GroupTicketListItem) => ticket.id,
    sortComparer: false // No default sorting
  });

/**
 * Extended state interface for ticket lists with entity management
 */
export interface TicketListEntityState extends EntityState<TicketsScope.TicketItem> {
  totalItems: number;
  isLoading: boolean;
  pagination: TicketsScope.Pagination;
}

export interface GroupTicketEntityState extends EntityState<TicketsScope.GroupTicketListItem> {
  totalItems: number;
  isLoading: boolean;
  pagination: TicketsScope.Pagination;
}

/**
 * Initial states for each list type
 */
export const initialSingleTicketState: TicketListEntityState = singleTicketAdapter.getInitialState({
  totalItems: 0,
  isLoading: false,
  pagination: {
    type: 'all',
    page: 1,
    per: 20,
    filters: {
      is_solo_bet: true,
      with_offer: false,
      status: 'live'
    }
  }
});

export const initialSyndicateTicketState: TicketListEntityState = syndicateTicketAdapter.getInitialState({
  totalItems: 0,
  isLoading: false,
  pagination: {
    type: 'all',
    page: 1,
    per: 20,
    filters: {
      is_solo_bet: false,
      with_offer: false,
      status: 'live'
    }
  }
});

export const initialGroupTicketState: GroupTicketEntityState = groupTicketAdapter.getInitialState({
  totalItems: 0,
  isLoading: false,
  pagination: {
    type: 'all',
    page: 1,
    per: 20,
    filters: {
      is_solo_bet: false,
      with_offer: false,
      status: 'live'
    }
  }
});

/**
 * Entity selectors for each adapter
 */
export const singleTicketSelectors = singleTicketAdapter.getSelectors();
export const syndicateTicketSelectors = syndicateTicketAdapter.getSelectors();
export const groupTicketSelectors = groupTicketAdapter.getSelectors();
